// Dedicated summarization pass, run after each mentor turn. Its jobs:
// (a) the inferred session objective as one sentence, (b) an updated organic
// prose report of the whole discussion, (c) update the session's own
// structured medium/long-term memory (cantiere/fondamenta below). Split out
// from the mentor's main answer because small SLMs reliably drop long/
// structured fields when they're one of many keys in a big JSON response — a
// dedicated, focused call keeps this coherent and genuinely incremental
// (prev state in, extended state out).
import { extractAndParseJSON } from "./jsonRepair";
import { render, renderStr, vocab } from "./prompts";

// Two-tier session memory, layered on top of the short-term chat window
// (the last few raw messages, already capped — see Chatbot.jsx's
// .slice(-6)/.slice(-3) — and reused unchanged by the regenerate/resync path,
// see InfiniteCanvas.js's regenerateSessionSummary):
//
// - MEDIO TERMINE ("cantiere delle idee"): still-in-motion working memory —
//   what's being explored right now. Expected to keep changing turn to turn.
// - LUNGO TERMINE ("fondamenta"): settled, load-bearing memory — decisions
//   and constraints that, once true, very rarely need to change again.
export const EMPTY_CANTIERE = { mappa: [], prossimi_passi: [] };
export const EMPTY_FONDAMENTA = { vincoli: [], preferenze_metodo: [] };

const CANTIERE_CAPS = { mappa: 12, prossimi_passi: 6 };
const FONDAMENTA_CAPS = { vincoli: 8, preferenze_metodo: 5 };
export const DECISIONS_CAP = 20;
// How many open decisions may be LISTED in the summarize prompt. This is a
// prompt-budget limit (the local-SLM path only has ~400 tokens for the whole
// pass — a long listing crowds out the exchange being summarized), entirely
// distinct from DECISIONS_CAP above, which caps what is STORED.
export const DECISIONS_PROMPT_CAP = 12;

// Trims the open-decision listing shown in the summarize prompt to at most
// `cap` entries — a PROMPT-BUDGET limit, distinct from DECISIONS_CAP's
// storage limit. `confermata` entries win the slots over `proposta` ones (a
// burst of unconfirmed proposals must never crowd out a confirmed decision
// the model may need to reference or supersede); within each group the most
// recent entries are kept. Original order is preserved in the output.
// Mirrors backend/pocketflow_agents.py's cap_decisions_for_prompt.
export function capDecisionsForPrompt(openDecisions, cap = DECISIONS_PROMPT_CAP) {
  if (openDecisions.length <= cap) return [...openDecisions];
  const confirmed = [];
  const others = [];
  openDecisions.forEach((d, i) => (d.status === "confermata" ? confirmed : others).push(i));
  const keep = new Set(confirmed.slice(-cap));
  const room = cap - keep.size;
  if (room > 0) others.slice(-room).forEach((i) => keep.add(i));
  return openDecisions.filter((_, i) => keep.has(i));
}

export function buildSummarizeMessages({
  prevSummary = "", prevObjective = "", exchange = "", cards = [],
  prevCantiere = null, prevFondamenta = null, prevDecisions = null, lang = "it",
}) {
  const v = vocab(lang);
  const cardList = cards.length ? cards.map((c) => c.label).join(", ") : v.none;
  const cantiere = prevCantiere || EMPTY_CANTIERE;
  const fondamenta = prevFondamenta || EMPTY_FONDAMENTA;
  const openDecisions = capDecisionsForPrompt(
    (prevDecisions || []).filter((d) => d.status !== "scartata" && d.status !== "superata")
  );
  const decisionsStr = openDecisions.length
    ? openDecisions.map((d) => `[${d.id}] (${d.status}) ${d.text}`).join("\n")
    : v.none_yet;
  const system = render("summarize_js_system", lang);
  const user = renderStr("summarize", lang, {
    prev_summary: prevSummary || v.empty_report,
    prev_objective: prevObjective || v.objective_unclear,
    cards: cardList,
    cantiere_json: JSON.stringify(cantiere),
    fondamenta_json: JSON.stringify(fondamenta),
    decisions: decisionsStr,
    exchange,
  });
  return [
    { role: "system", content: system },
    { role: "user", content: user },
  ];
}

// Grammar constraint for the local (WebLLM/Engine) path.
export const SUMMARIZE_SCHEMA = JSON.stringify({
  type: "object",
  properties: {
    obiettivo: { type: "string" },
    sintesi: { type: "string" },
    cantiere: {
      type: "object",
      properties: {
        mappa: { type: "array", items: { type: "string" } },
        prossimi_passi: { type: "array", items: { type: "string" } },
      },
      required: ["mappa", "prossimi_passi"],
    },
    fondamenta: {
      type: "object",
      properties: {
        vincoli: { type: "array", items: { type: "string" } },
        preferenze_metodo: { type: "array", items: { type: "string" } },
      },
      required: ["vincoli", "preferenze_metodo"],
    },
    nuove_decisioni: {
      type: "array",
      items: {
        type: "object",
        properties: {
          testo: { type: "string" }, motivazione: { type: "string" },
          origine: { type: "string" }, carta: { type: "string" },
          phase: { type: "string" }, supera_id: { type: "string" },
        },
        required: ["testo"],
      },
    },
    aggiorna_decisioni: {
      type: "array",
      items: {
        type: "object",
        properties: { id: { type: "string" }, stato: { type: "string" }, motivazione: { type: "string" } },
        required: ["id", "stato"],
      },
    },
  },
  required: ["obiettivo", "sintesi", "cantiere", "fondamenta", "nuove_decisioni", "aggiorna_decisioni"],
});

function cleanStringArray(v) {
  return Array.isArray(v) ? v.filter((s) => typeof s === "string" && s.trim()).map((s) => s.trim()) : [];
}

export function parseSummarize(raw, { currentPhase = "esplorazione_ideazione", validPhases = [] } = {}) {
  const j = extractAndParseJSON(raw);
  if (!j) {
    return {
      obiettivo: "", sintesi: "", cantiere: EMPTY_CANTIERE, fondamenta: EMPTY_FONDAMENTA,
      nuoveDecisioni: [], aggiornaDecisioni: [],
    };
  }
  return {
    obiettivo: typeof j.obiettivo === "string" ? j.obiettivo.trim() : "",
    sintesi: typeof j.sintesi === "string" ? j.sintesi.trim() : "",
    cantiere: {
      mappa: cleanStringArray(j.cantiere?.mappa),
      prossimi_passi: cleanStringArray(j.cantiere?.prossimi_passi),
    },
    fondamenta: {
      vincoli: cleanStringArray(j.fondamenta?.vincoli),
      preferenze_metodo: cleanStringArray(j.fondamenta?.preferenze_metodo),
    },
    nuoveDecisioni: validateDecisions(j.nuove_decisioni, currentPhase, validPhases),
    aggiornaDecisioni: validateDecisionUpdates(j.aggiorna_decisioni),
  };
}

// Case-insensitive dedup append, capped — same normalize-then-Set pattern
// slmMentor.js already uses for the flat evidence memory array. Medio-
// termine entries are expected to churn (superseded items just stop being
// re-emitted by the model and age out via the cap), so this only ever grows
// forward; nothing here prunes on its own.
function mergeUnique(prev, incoming, cap) {
  const seen = new Set(prev.map((s) => s.toLowerCase()));
  const merged = [...prev];
  for (const s of incoming) {
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(s);
  }
  return merged.slice(-cap);
}

// Folds a summarize-pass delta into the session's running cantiere — pure,
// so the merge policy (dedup + cap, not overwrite) is independently testable
// from the LLM call around it.
export function mergeCantiere(prev, delta) {
  const p = prev || EMPTY_CANTIERE;
  const d = delta || EMPTY_CANTIERE;
  return {
    mappa: mergeUnique(p.mappa || [], d.mappa || [], CANTIERE_CAPS.mappa),
    prossimi_passi: mergeUnique(p.prossimi_passi || [], d.prossimi_passi || [], CANTIERE_CAPS.prossimi_passi),
  };
}

// vincoli/preferenze_metodo dedup-append. "vincitore" moved to the decision
// log (see mergeDecisions) — a superseded core decision is now tracked as
// superata instead of silently overwritten.
export function mergeFondamenta(prev, delta) {
  const p = prev || EMPTY_FONDAMENTA;
  const d = delta || EMPTY_FONDAMENTA;
  return {
    vincoli: mergeUnique(p.vincoli || [], d.vincoli || [], FONDAMENTA_CAPS.vincoli),
    preferenze_metodo: mergeUnique(p.preferenze_metodo || [], d.preferenze_metodo || [], FONDAMENTA_CAPS.preferenze_metodo),
  };
}

// Coerces malformed nuove_decisioni entries from the summarize-pass model
// output — mirrors backend/pocketflow_agents.py's validate_decisions.
// `carta` only needs to be a non-empty string (matches carte_collegate's
// existing leniency elsewhere — no check against the live card-label list).
export function validateDecisions(nuoveDecisioni, currentPhase, validPhases) {
  const out = [];
  for (const d of (Array.isArray(nuoveDecisioni) ? nuoveDecisioni : [])) {
    if (!d || typeof d !== "object") continue;
    const text = typeof d.testo === "string" ? d.testo.trim() : "";
    if (!text) continue;
    const motivazione = typeof d.motivazione === "string" ? d.motivazione.trim() : "";
    const origine = d.origine === "mentor" ? "mentor" : "utente";
    const carta = typeof d.carta === "string" && d.carta.trim() ? d.carta.trim() : null;
    const phase = validPhases.includes(d.phase) ? d.phase : currentPhase;
    const superaId = typeof d.supera_id === "string" && d.supera_id.trim() ? d.supera_id.trim() : null;
    out.push({ text, motivazione, origine, carta, phase, superaId });
  }
  return out;
}

// Coerces malformed aggiorna_decisioni entries. `stato` is restricted to
// confermata/scartata — "superata" is backend-only and silently dropped if
// the model returns it (see mergeDecisions). Mirrors
// backend/pocketflow_agents.py's validate_decision_updates.
export function validateDecisionUpdates(aggiornaDecisioni) {
  const out = [];
  for (const u of (Array.isArray(aggiornaDecisioni) ? aggiornaDecisioni : [])) {
    if (!u || typeof u !== "object") continue;
    const id = typeof u.id === "string" ? u.id.trim() : "";
    if (!id) continue;
    const stato = u.stato === "confermata" || u.stato === "scartata" ? u.stato : null;
    if (!stato) continue;
    const motivazione = typeof u.motivazione === "string" ? u.motivazione.trim() : "";
    out.push({ id, stato, motivazione });
  }
  return out;
}

// Folds a summarize-pass delta into the session's running decision log —
// pure, mirrors mergeCantiere/mergeFondamenta and
// backend/pocketflow_agents.py's merge_decisions. `newDecisions`/`updates`
// must already be validated (validateDecisions/validateDecisionUpdates).
// Ids are assigned here, sequentially, never by the model. "superata" is
// set only here (never accepted from `updates`) when a new decision's
// superaId resolves to an existing confermata entry.
export function mergeDecisions(prev, newDecisions, updates) {
  const prevList = prev || [];
  const byId = new Map(prevList.map((d) => [d.id, { ...d }]));
  const order = prevList.map((d) => d.id);

  for (const u of updates) {
    const entry = byId.get(u.id);
    if (!entry) continue;
    entry.status = u.stato;
    if (u.motivazione) entry.motivazione = u.motivazione;
  }

  // Derive the next id from the HIGHEST id ever issued, not from list
  // length: the cap prunes terminal entries, so length-based numbering
  // would reissue an id still held by a live entry and overwrite it.
  const nums = prevList.map((d) => parseInt(String(d.id).slice(1), 10)).filter(Number.isInteger);
  let nextNum = nums.length ? Math.max(...nums) + 1 : 1;
  for (const d of newDecisions) {
    const newId = `d${nextNum}`;
    nextNum += 1;
    const entry = {
      id: newId, text: d.text, status: "proposta",
      motivazione: d.motivazione, origine: d.origine,
      carta: d.carta, phase: d.phase,
      supersedes: null, supersededBy: null,
    };
    if (d.superaId && byId.has(d.superaId) && byId.get(d.superaId).status === "confermata") {
      const old = byId.get(d.superaId);
      old.status = "superata";
      old.supersededBy = newId;
      entry.supersedes = d.superaId;
    }
    byId.set(newId, entry);
    order.push(newId);
  }

  let merged = order.map((id) => byId.get(id));
  const excess = merged.length - DECISIONS_CAP;
  if (excess > 0) {
    const terminalIdx = [];
    merged.forEach((d, i) => { if (d.status === "scartata" || d.status === "superata") terminalIdx.push(i); });
    const drop = new Set(terminalIdx.slice(0, excess));
    merged = merged.filter((_, i) => !drop.has(i));
  }
  return merged;
}

// The summarize pass is a second LLM round-trip and can fail on its own
// (quota/overload) even when the main mentor call succeeded — that failure is
// non-fatal and silent by design, which left the brief permanently blank on a
// bad first turn. Seeds obiettivo/sintesi_processo directly from `purpose` (no
// extra LLM call) when both are still empty, so the brief shows something
// immediately; a later successful summarize pass still overwrites this with
// the real discorsivo report. Mutates and returns `brief`.
export function bootstrapBrief(brief, purpose, lang = "it") {
  const p = (purpose || "").trim();
  if (!p) return brief;
  if (!brief.obiettivo) {
    const words = p.split(/\s+/);
    brief.obiettivo = words.slice(0, 5).join(" ") + (words.length > 5 ? "…" : "");
  }
  if (!brief.sintesi_processo) {
    brief.sintesi_processo = `${vocab(lang).session_started_prefix}${p}`;
  }
  if (!brief.cantiere) brief.cantiere = EMPTY_CANTIERE;
  if (!brief.fondamenta) brief.fondamenta = EMPTY_FONDAMENTA;
  if (!brief.decisions) brief.decisions = [];
  return brief;
}
